import { useCallback, useId } from 'react';

import { useQuickSendSettings } from '@/hooks/useQuickSendSettings';
import { useT } from '@/i18n';
import type { Shortcut } from '@/lib/macKeys';
import { quickSendAvailable } from '@/lib/quickSend';

import { Button } from './Button';
import styles from './QuickSendSettings.module.css';
import { ShortcutBlock } from './ShortcutBlock';
import { SwitchRow } from './SwitchRow';
import ui from './ui.module.css';

/**
 * The Mac app's global shortcuts in the settings sheet: send a secret, open a private chat; allow Bridge to copy a
 * selection; open Bridge at login. Where the shell does not offer them (a browser, iPhone, iPad) it draws nothing.
 */
export function QuickSendSettings(): React.JSX.Element | null {
  const { state, setEnabled, setRecording, allowAccessibility, setLogin, checkUpdates } = useQuickSendSettings();
  const t = useT();
  const id = useId();
  const setSend = useCallback((enabled: boolean, shortcut: Shortcut): void => {
    setEnabled('send', enabled, shortcut);
  }, [setEnabled]);
  const setChat = useCallback((enabled: boolean, shortcut: Shortcut): void => {
    setEnabled('chat', enabled, shortcut);
  }, [setEnabled]);

  if (!quickSendAvailable() || state === null) return null;
  return (
    <div className={styles.group} data-testid="quick-send-settings">
      <div className={styles.section}>
        <h3 id={`${id}-shortcuts`} className={ui.fieldLabel}>
          {t('quickSendSettings.title')}
        </h3>
        <p className={ui.fieldHint}>{t('quickSendSettings.intro')}</p>
        <div className={styles.rows} role="group" aria-labelledby={`${id}-shortcuts`}>
          <ShortcutBlock
            label={t('quickSendSettings.sendLabel')}
            hint={t('quickSendSettings.sendHint')}
            setting={state}
            problem={state.problemTarget === 'send' ? state.problem : null}
            onSet={setSend}
            onRecording={setRecording}
            testId="quick-send"
          />
          <ShortcutBlock
            label={t('quickSendSettings.chatLabel')}
            hint={t('quickSendSettings.chatHint')}
            setting={state.chat}
            problem={state.problemTarget === 'chat' ? state.problem : null}
            onSet={setChat}
            onRecording={setRecording}
            testId="chat-shortcut"
          />
        </div>
        {!state.enabled ? null : !state.copiesSelection ? (
          <p className={ui.fieldHint}>{t('quickSendSettings.sendsClipboard')}</p>
        ) : state.accessibility ? (
          <p className={ui.fieldHint}>{t('quickSendSettings.canRead')}</p>
        ) : (
          <>
            <p className={ui.fieldHint}>{t('quickSendSettings.noAccessibility')}</p>
            <div>
              <Button variant="secondary" size="small" onClick={allowAccessibility} data-testid="quick-send-accessibility">
                {t('quickSendSettings.allow')}
              </Button>
            </div>
          </>
        )}
      </div>
      <SwitchRow
        label={t('quickSendSettings.loginLabel')}
        hint={state.login === 'approval' ? t('quickSendSettings.loginApprove') : t('quickSendSettings.loginHint')}
        checked={state.login !== 'off'}
        onChange={setLogin}
        data-testid="quick-send-login"
      />
      {!state.updates ? null : (
        <div className={styles.section} data-testid="updates-settings">
          <h3 id={`${id}-updates`} className={ui.fieldLabel}>
            {t('quickSendSettings.updatesLabel')}
          </h3>
          <p className={ui.fieldHint}>{t('quickSendSettings.updatesHint')}</p>
          <div>
            <Button variant="secondary" size="small" onClick={checkUpdates} aria-describedby={`${id}-updates`} data-testid="check-updates">
              {t('quickSendSettings.updatesButton')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
